import React from "react";
import { ChakraProvider } from '@chakra-ui/react';
import { Routes, Route, Navigate } from "react-router-dom";
import { Box, Flex, Text, useColorModeValue } from "@chakra-ui/react";
import Sidebar from "../components/Sidebar";
import SidebarResponsive from "../components/Sidebar/SidebarResponsive";
import AdminNavbar from "../components/Navbars/AdminNavbar";
import ProtectedRoute from "../components/ProtectedRoute";
import routes from "../routes";
import Dashboard from "../views/Dashboard";
import ConsultantDashboard from "../views/ConsultantDashboard";
import Clients from "../views/Clients";
import Consultants from "../views/Consultants";
import Users from "../views/Users";
import Appointments from "../views/Appointments";
import Files from "../views/Files";
import Reports from "../views/Reports";
import Activities from "../views/Activities";
import Settings from "../views/Settings";
import Profile from "../views/Profile";
import theme from '../theme/theme.js';
import { useAuth } from '../contexts/AuthContext';
import ClientProfile from "../views/ClientProfile";
import MyClients from "../views/MyClients";
import WorkingHours from "../views/WorkingHours";
import UnavailableHours from "../views/UnavailableHours";
import FormBuilder from "../views/FormBuilder";
import FormEditor from "../views/FormEditor";
import FormResponses from "../views/FormResponses";
import AppointmentDetails from "../views/AppointmentDetails";
import MobileBottomNav from "../components/MobileBottomNav";
import '../styles/scroll.css';

export default function RTLLayout() {
  const { user, isConsultant, isAdmin } = useAuth();
  const textColor = useColorModeValue("gray.700", "white");
  
  // Set RTL direction
  document.documentElement.dir = 'rtl';
  
  // تعیین مسیر پیش‌فرض بر اساس نقش کاربر
  const getDefaultRoute = () => {
    if (isConsultant()) {
      return "/consultant-dashboard";
    }
    return "/dashboard";
  };
  
  return (
    <Box>
      <Sidebar routes={routes} logoText={"پنل مشاور"} sidebarVariant="opaque" />
      {/* Main Content Area */}
      <Box
        float="left"
        w={{ base: "100%", xl: "calc(100% - 275px)" }}
        position="relative"
        minH="100vh"
        bg="gray.50"
      >
        {/* Fixed Header - با inline styles */}
        <Box 
          className="fixed-header transparent-header"
          style={{
            position: 'fixed',
            top: 0,
            right: '275px',
            left: 0,
            zIndex: 1000,
            backgroundColor: 'transparent',
            borderBottom: 'none',
            boxShadow: 'none',
            display: 'block'
          }}
          display={{ base: "none", lg: "block" }}
        >
          <Box mx="auto" p={{ base: "15px", md: "20px" }} pe="20px">
            <Flex
              w="100%"
              justifyContent="space-between"
              alignItems="center"
              mb="10px"
              display={{ base: "none", lg: "flex" }}
            >
              <Box>
                <SidebarResponsive routes={routes} />
              </Box>
            </Flex>
            <AdminNavbar
              onOpen={() => {}}
              logoText={"پنل مشاور"}
              brandText={isConsultant() ? "داشبورد مشاور" : "داشبورد"}
              secondary={false}
              fixed={false}
            />
          </Box>
        </Box>
        
        {/* Scrollable Content - با inline styles */}
        <Box 
          style={{
            paddingTop: '100px',
            paddingLeft: '20px',
            paddingRight: '20px',
            paddingBottom: '30px',
            minHeight: '100vh',
            overflow: 'auto'
          }}
          className="content-scroll"
          pt={{ base: "20px", lg: "100px" }}
          px={{ base: "20px", md: "30px" }}
          pb={{ base: "80px", lg: "30px" }}
          minH="100vh"
          overflow="auto"
        >
              {/* Mobile Title */}
              <Box display={{ base: "block", lg: "none" }} mb={4}>
                <Text fontSize="xl" fontWeight="bold" color={textColor} textAlign="center">
                  {isConsultant() ? "پنل مشاور" : "کلینیک مشاوره"}
                </Text>
              </Box>
              
              {/* Routes */}
              <Routes>
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'manager']}>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route path="/consultant-dashboard" element={<ProtectedRoute><ConsultantDashboard /></ProtectedRoute>} />
                <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
                <Route path="/consultants" element={<ProtectedRoute><Consultants /></ProtectedRoute>} />
                <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
                <Route path="/appointments" element={<ProtectedRoute><Appointments /></ProtectedRoute>} />
                <Route path="/my-appointments" element={<ProtectedRoute><Appointments /></ProtectedRoute>} />
                <Route path="/files" element={<ProtectedRoute><Files /></ProtectedRoute>} />
                <Route path="/activities" element={<ProtectedRoute><Activities /></ProtectedRoute>} />
                <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/client-profile/:id" element={<ProtectedRoute><ClientProfile /></ProtectedRoute>} />
                <Route path="/appointment/:id" element={<ProtectedRoute><AppointmentDetails /></ProtectedRoute>} />
                <Route path="/my-clients" element={<ProtectedRoute><MyClients /></ProtectedRoute>} />
                <Route path="/working-hours" element={<ProtectedRoute><WorkingHours /></ProtectedRoute>} />
                <Route path="/unavailable-hours" element={<ProtectedRoute><UnavailableHours /></ProtectedRoute>} />
                <Route path="/form-builder" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><FormBuilder /></ProtectedRoute>} />
                <Route path="/form-builder/new" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><FormEditor /></ProtectedRoute>} />
                <Route path="/form-builder/:id/edit" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><FormEditor /></ProtectedRoute>} />
                <Route path="/form-builder/:id/responses" element={<ProtectedRoute allowedRoles={['admin', 'manager']}><FormResponses /></ProtectedRoute>} />
                <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
              </Routes>
            </Box>
      </Box>
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </Box>
  );
}
